/**
 * useGpsLocation — uses @capacitor/geolocation directly for both native and web.
 *
 * On NATIVE (Android/iOS):
 *   - Checks permission, requests if needed, gets position via Capacitor plugin.
 *   - Uses useGeolocation's LocationSettingsPlugin for the Android GPS-toggle dialog.
 *
 * On WEB (browser):
 *   - Uses navigator.geolocation.getCurrentPosition() directly.
 *   - The browser's own permission dialog will appear automatically.
 *   - First checks permission state. If already denied, logs and returns null.
 *
 * Returns { latitude, longitude, accuracy } or null on any failure (never throws).
 */
import { Geolocation } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';
import { useCallback } from 'react';
import useGeolocation from './useGeolocation';

const useGpsLocation = () => {
  // Pull in the native-only helpers from PR #6's rich hook
  const { checkLocationEnabled, requestEnableLocation } = useGeolocation();

  const isNative = Capacitor.isNativePlatform();

  /**
   * Get device GPS coordinates.
   * Silently returns null on any failure — never throws.
   *
   * @returns {Promise<{ latitude: number, longitude: number, accuracy: number } | null>}
   */
  const getLocation = useCallback(async () => {
    try {
      if (isNative) {
        // ── NATIVE PATH ────────────────────────────────────────────────────────
        // 1. Make sure the device GPS toggle is on (Android in-app prompt)
        const locationEnabled = await checkLocationEnabled();
        if (!locationEnabled) {
          const enabled = await requestEnableLocation();
          if (!enabled) return null;
        }

        // 2. Check & request permission
        let permStatus;
        try {
          const perm = await Geolocation.checkPermissions();
          permStatus = perm.location;
        } catch {
          permStatus = 'prompt';
        }

        if (permStatus === 'prompt' || permStatus === 'prompt-with-rationale') {
          try {
            const perm = await Geolocation.requestPermissions();
            permStatus = perm.location;
          } catch {
            permStatus = 'denied';
          }
        }

        if (permStatus !== 'granted') {
          console.warn('[useGpsLocation] Permission not granted:', permStatus);
          return null;
        }

        // 3. Get position
        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 10000,
        });

        return {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        };
      } else {
        // ── WEB / BROWSER PATH ────────────────────────────────────────────────
        // Check if geolocation is supported at all
        if (!('geolocation' in navigator)) {
          console.warn('[useGpsLocation] navigator.geolocation not available');
          return null;
        }

        // Check the current permission state via the Permissions API (read-only)
        if (navigator.permissions) {
          try {
            const permResult = await navigator.permissions.query({ name: 'geolocation' });
            if (permResult.state === 'denied') {
              console.warn(
                '[useGpsLocation] Geolocation is blocked in browser settings.',
                'To fix: click the 🔒 icon in the address bar and allow location for this site.'
              );
              return null;
            }
            // 'granted' → dialog won't show (already allowed), coordinates returned silently
            // 'prompt'  → dialog WILL show when getCurrentPosition is called below
          } catch {
            // Permissions API not available — continue anyway
          }
        }

        // Call browser geolocation — this triggers the permission dialog when state is 'prompt'
        // NOTE: enableHighAccuracy: false uses WiFi/network triangulation (fast, works on laptops).
        //       enableHighAccuracy: true waits for a real GPS satellite fix — laptops don't have
        //       GPS chips, so this times out immediately. Always use false for web.
        return await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              resolve({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy,
              });
            },
            (err) => {
              console.warn('[useGpsLocation] Browser geolocation error:', err.message);
              resolve(null); // non-blocking: always resolve with null on failure
            },
            {
              enableHighAccuracy: false, // network-based (fast) — fine for mart detection
              timeout: 15000,            // 15s is plenty for WiFi-based location
              maximumAge: 300000,        // accept a cached fix up to 5 min old
            }
          );
        });
      }
    } catch (err) {
      console.warn('[useGpsLocation] Unexpected error:', err);
      return null;
    }
  }, [isNative, checkLocationEnabled, requestEnableLocation]);

  return { getLocation };
};

export default useGpsLocation;
